import type { IconType } from 'react-icons';
import {
    LuKanban, LuContact, LuBuilding2, LuListTodo, LuCalendar, LuNotebookPen,
    LuSparkles, LuPackage, LuSend, LuGoal, LuUsers, LuUsersRound, LuCircleHelp,
    LuSun, LuMoon, LuLogOut, LuPlus, LuRocket, LuCreditCard, LuSettings,
    LuHouse, LuFileText, LuMessageSquare, LuPlug } from 'react-icons/lu';

import { apiFetch } from '../services/api';
import { OpportunityService } from '../services/OpportunityService';
import { ContactService } from '../services/ContactService';
import { OrganizationService } from '../services/OrganizationService';
import { TarefaService } from '../services/TarefaService';
import { AnotacaoService, tituloDaAnotacao } from '../services/AnotacaoService';
import { AgendaService } from '../services/AgendaService';
import { ProductService } from '../services/ProductService';
import { CampaignService } from '../services/CampaignService';
import EquipeService from '../services/EquipeService';
import MetaGoalService from '../services/MetaGoalService';
import AssistenteService from '../services/AssistenteService';
import { HelpService } from '../services/HelpService';
import { GRUPOS_OPERACAO } from './navegacao';
import { GRUPOS_CONFIG, ehSubmenu } from './configuracoes';

/**
 * O MAPA DA BUSCA GLOBAL — o que o Ctrl+K alcança.
 *
 * Proposta de 2026-10-01 (`docs/propostas/busca-global-ctrl-k.html`), Direção A,
 * com o alcance que ele pediu: **tudo**. Entidade, tela e ação saem todas daqui;
 * a paleta (`components/BuscaGlobal.tsx`) só desenha e navega.
 *
 * Por que um registro em vez de escrever cada grupo dentro do componente: o
 * pedido foi "busca e ações a tudo na plataforma", e "tudo" envelhece sozinho.
 * Entidade nova entra com UMA entrada aqui — rótulo, ícone, como carregar, onde
 * o resultado abre — e aparece na paleta, no celular, no teclado e na contagem
 * sem ninguém tocar no componente. É a mesma razão de `navegacao.ts` ser fonte
 * única: lista escrita duas vezes diverge, e quem perde é quem procura.
 *
 * **Permissão não mora aqui, de propósito.** Todo resultado da paleta é uma
 * navegação, e quem decide se uma rota é alcançável já é
 * `canAccessRoute(rota, permissoes)` — que também respeita funcionalidade
 * desligada (`ROUTE_FEATURES`). A paleta chama essa função e pronto: um grupo
 * cuja rota a pessoa não alcança nem é carregado, então a busca também não
 * vaza dado de tela que ela não vê.
 */

/* ─── O QUE A PALETA MOSTRA ───────────────────────────────────── */

export interface ResultadoBusca {
    /** Único no app inteiro — vira a `key` e o alvo do teclado. */
    chave: string;
    titulo: string;
    /** A segunda linha. O que identifica sem precisar abrir. */
    subtitulo?: string;
    /** Pílula à direita: status, estado, situação. */
    etiqueta?: string;
    tom?: 'ok' | 'aviso' | 'erro' | 'neutro';
    to: string;
    /** Os campos onde o texto digitado é procurado. */
    termos: (string | null | undefined)[];
}

export interface FonteBusca {
    id: string;
    /** Cabeçalho do grupo, no plural. */
    grupo: string;
    icone: IconType;
    /** Rota-base, para `canAccessRoute` decidir se o grupo existe para esta pessoa. */
    rota: string;
    /**
     * Carrega os registros. Chamada **uma vez por sessão** para as fontes de
     * lista; as que buscam no servidor (`servidor: true`) recebem o texto e são
     * chamadas a cada busca, depois da espera.
     */
    carregar: (texto: string) => Promise<ResultadoBusca[]>;
    servidor?: boolean;
    /**
     * Para onde vai o "Ver todos". `chave` é a do recorte em `sessionStorage`
     * (ver `hooks/useRecorte`) e `campo`, o nome do filtro de texto dentro dele
     * — é assim que o termo digitado chega à listagem **sem tocar na tela**.
     * Sem isto, o grupo não ganha a linha de "ver todos": prometer a lista
     * cheia e entregá-la sem o filtro é pior do que não prometer.
     */
    verTodos?: { to: string; chave: string; campo?: string };
}

/* ─── NORMALIZAÇÃO ────────────────────────────────────────────── */

/** Sem acento e sem caixa: "Joao" acha "João", "Analise" acha "Análise". */
export function normalizar(texto: string): string {
    return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Só os dígitos — é como se procura telefone sem depender da máscara. */
const digitos = (texto: string) => texto.replace(/\D/g, '');

export function corresponde(procurado: string, termos: ResultadoBusca['termos']): boolean {
    if (!procurado) return true;
    const alvo = normalizar(procurado);
    const numero = digitos(procurado);
    return termos.some(termo => {
        if (!termo) return false;
        if (normalizar(termo).includes(alvo)) return true;
        /* Telefone digitado com máscara, salvo sem — e vice-versa. Dois dígitos
           não bastam: casariam com quase todo número da base. */
        return numero.length >= 3 && digitos(termo).includes(numero);
    });
}

/* ─── AJUDANTES DE FORMATO ────────────────────────────────────── */

const soData = (iso: string | null | undefined): string | undefined => {
    if (!iso) return undefined;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? undefined : d.toLocaleDateString('pt-BR');
};

const dataEHora = (iso: string | null | undefined): string | undefined => {
    if (!iso) return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const emReais = (valor: number | null | undefined): string | undefined =>
    typeof valor === 'number' && valor > 0
        ? valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
        : undefined;

/** Junta os pedaços que existem com " · ". Nada de separador solto. */
const linha = (...partes: (string | null | undefined)[]) => {
    const vivas = partes.filter((p): p is string => !!p && p.trim() !== '');
    return vivas.length ? vivas.join(' · ') : undefined;
};

/** O tom da pílula de uma oportunidade, pela situação. */
function tomDoStatus(descricao: string | undefined): ResultadoBusca['tom'] {
    const s = normalizar(descricao ?? '');
    if (s.includes('ganho') || s.includes('fechada') || s.includes('vendido')) return 'ok';
    if (s.includes('perdid')) return 'erro';
    return 'neutro';
}

/* ─── AS FONTES ───────────────────────────────────────────────── */

/**
 * A ordem aqui é a ordem na tela, e ela é deliberada: primeiro o que se procura
 * pelo nome de uma pessoa (oportunidade, contato, organização), depois o
 * trabalho do dia (tarefa, agenda, anotação), depois os cadastros, e a ajuda por
 * último. Quem digita um nome quer o negócio, não o produto que por acaso se
 * chama igual.
 */
export const FONTES: FonteBusca[] = [
    {
        id: 'oportunidades',
        grupo: 'Oportunidades',
        icone: LuKanban,
        rota: '/pipeline',
        verTodos: { to: '/pipeline', chave: 'pl:filtros' },
        carregar: async () => {
            // Navegação, não métrica: acha também a conversa de contato fora do funil.
            const itens = await OpportunityService.getAllOpportunities({ incluirForaDoFunil: true });
            return itens.map(o => ({
                chave: `op-${o.idConversa}`,
                titulo: o.nomeContato || o.numeroWhatsapp || `Negócio ${o.idConversa}`,
                subtitulo: linha(o.numeroWhatsapp, o.nomeOrganizacao, emReais(o.valor),
                    o.nomeUsuarioResponsavel),
                etiqueta: o.foraDoFunil ? 'Fora do funil' : o.descricaoStatus,
                tom: o.foraDoFunil ? 'neutro' : tomDoStatus(o.descricaoStatus),
                to: `/pipeline/deal/${o.idConversa}`,
                termos: [o.nomeContato, o.numeroWhatsapp, o.nomeOrganizacao,
                    o.nomeCampanha, o.nomeUsuarioResponsavel, o.descricaoStatus],
            }));
        },
    },
    {
        id: 'contatos',
        grupo: 'Contatos',
        icone: LuContact,
        rota: '/contacts',
        verTodos: { to: '/contacts', chave: 'ct:recorte' },
        carregar: async () => {
            const r = await ContactService.getAll();
            const itens = r.sucesso && Array.isArray(r.dados) ? r.dados : [];
            return itens.map(c => ({
                chave: `ct-${c.contatoId}`,
                titulo: c.nome || c.telefone,
                subtitulo: linha(c.telefone, c.email, c.organizacaoNome, c.city),
                to: '/contacts',
                termos: [c.nome, c.telefone, c.email, c.organizacaoNome, c.city, c.segment],
            }));
        },
    },
    {
        id: 'organizacoes',
        grupo: 'Organizações',
        icone: LuBuilding2,
        rota: '/organizations',
        verTodos: { to: '/organizations', chave: 'org:recorte' },
        carregar: async () => {
            const r = await OrganizationService.getAll();
            const itens = r.sucesso && Array.isArray(r.dados) ? r.dados : [];
            return itens.map(o => ({
                chave: `org-${o.organizacaoId}`,
                titulo: o.nome,
                subtitulo: linha(
                    o.nomeFantasia,
                    o.quantidadePessoas ? `${o.quantidadePessoas} pessoa${o.quantidadePessoas > 1 ? 's' : ''}` : null,
                    o.cidade,
                ),
                to: '/organizations',
                termos: [o.nome, o.nomeFantasia, o.cnpj, o.cidade, o.segmento, o.email, o.telefone],
            }));
        },
    },
    {
        id: 'tarefas',
        grupo: 'Tarefas',
        icone: LuListTodo,
        rota: '/tarefas',
        verTodos: { to: '/tarefas', chave: 'tf:recorte' },
        carregar: async () => {
            const itens = await TarefaService.getAll({});
            return itens.map(t => ({
                chave: `tf-${t.tarefaId}`,
                titulo: t.descricao,
                subtitulo: linha(
                    t.nomeContato,
                    t.dataRetorno ? `para ${soData(t.dataRetorno)}` : null,
                ),
                etiqueta: t.estaConcluida ? 'Concluída' : 'Aberta',
                tom: t.estaConcluida ? 'ok' : 'neutro',
                to: '/tarefas',
                termos: [t.descricao, t.nomeContato, t.desfechoRotulo],
            }));
        },
    },
    {
        id: 'agenda',
        grupo: 'Agenda',
        icone: LuCalendar,
        rota: '/agenda',
        carregar: async () => {
            const itens = await AgendaService.getAll();
            return itens.map(e => ({
                chave: `ag-${e.agendaId}`,
                titulo: e.descricao,
                subtitulo: linha(dataEHora(e.dataInicio), e.detalhes),
                to: '/agenda',
                termos: [e.descricao, e.detalhes],
            }));
        },
    },
    {
        id: 'anotacoes',
        grupo: 'Anotações',
        icone: LuNotebookPen,
        rota: '/anotacoes',
        verTodos: { to: '/anotacoes', chave: 'an:recorte' },
        carregar: async () => {
            const r = await AnotacaoService.getAll();
            const itens = r.sucesso && Array.isArray(r.dados) ? r.dados : [];
            return itens.map(a => ({
                chave: `an-${a.anotacaoId}`,
                titulo: tituloDaAnotacao(a),
                subtitulo: linha(a.nomeUsuarioCriador, soData(a.dataAtualizacao || a.dataCriacao)),
                to: `/anotacoes/${a.anotacaoId}`,
                /* O conteúdo entra na busca: anotação se procura pelo que está
                   escrita dentro, não pelo título que quase nunca tem. */
                termos: [a.titulo, a.conteudo, a.etiquetas, a.nomeUsuarioCriador],
            }));
        },
    },
    {
        id: 'conversas',
        grupo: 'Conversas do assistente',
        icone: LuSparkles,
        rota: '/assistente',
        carregar: async () => {
            /* Teto alto de propósito: o menu da sidebar mostra 5, mas aqui a
               graça é achar a conversa velha que não cabe no menu. */
            const itens = await AssistenteService.listarConversas(200);
            return itens.map(c => ({
                chave: `cv-${c.conversaId}`,
                titulo: c.titulo,
                subtitulo: soData(c.atualizadaEm || c.criadaEm),
                to: `/assistente/${c.conversaId}`,
                termos: [c.titulo],
            }));
        },
    },
    {
        id: 'produtos',
        grupo: 'Produtos',
        icone: LuPackage,
        rota: '/products',
        verTodos: { to: '/products', chave: 'pr:recorte' },
        carregar: async () => {
            const r = await ProductService.getAll();
            const itens = r.sucesso && Array.isArray(r.dados) ? r.dados : [];
            return itens.map(p => ({
                chave: `pr-${p.produtoId}`,
                titulo: p.nome,
                subtitulo: linha(p.codigo, p.marca, emReais(p.preco)),
                to: '/products',
                termos: [p.nome, p.codigo, p.marca],
            }));
        },
    },
    {
        id: 'campanhas',
        grupo: 'Campanhas',
        icone: LuSend,
        rota: '/campanhas',
        carregar: async () => {
            const r = await CampaignService.getAll();
            const itens = r.sucesso && Array.isArray(r.dados) ? r.dados : [];
            return itens.map(c => ({
                chave: `cp-${c.campanhaId}`,
                titulo: c.campanhaNome,
                subtitulo: linha(c.campanhaTemplateNome, soData(c.campanhaCreatedat)),
                to: `/campanhas/${c.campanhaId}`,
                termos: [c.campanhaNome, c.campanhaTemplateNome],
            }));
        },
    },
    {
        id: 'metas',
        grupo: 'Metas',
        icone: LuGoal,
        rota: '/metas',
        carregar: async () => {
            const itens = await MetaGoalService.buscarTodas();
            const rotulo = { value: 'Valor', quantity: 'Quantidade', calls: 'Ligações' };
            return itens.map(m => ({
                chave: `mt-${m.id}`,
                titulo: m.alvoNome,
                subtitulo: linha(rotulo[m.tipo] ?? m.tipo, m.mes,
                    m.tipo === 'value' ? emReais(m.valorAlvo) : `alvo ${m.valorAlvo}`),
                etiqueta: m.tipoAlvo === 'team' ? 'Equipe' : 'Pessoa',
                tom: 'neutro',
                to: '/metas',
                termos: [m.alvoNome, m.mes],
            }));
        },
    },
    {
        id: 'usuarios',
        grupo: 'Usuários',
        icone: LuUsers,
        rota: '/users',
        verTodos: { to: '/users', chave: 'us:recorte' },
        carregar: async () => {
            const res = await apiFetch(`${import.meta.env.VITE_API_URL}/api/Usuario/BuscarTodos`);
            const corpo = await res.json().catch(() => null);
            const itens: Record<string, unknown>[] = corpo?.sucesso ? (corpo.dados ?? []) : [];
            return itens.map(u => ({
                chave: `us-${u.userId}`,
                titulo: String(u.userName ?? ''),
                subtitulo: String(u.userEmail ?? '') || undefined,
                etiqueta: u.userDeleted ? 'Inativo' : undefined,
                tom: 'aviso' as const,
                to: '/users',
                termos: [String(u.userName ?? ''), String(u.userEmail ?? '')],
            }));
        },
    },
    {
        id: 'equipes',
        grupo: 'Equipes',
        icone: LuUsersRound,
        rota: '/equipes',
        verTodos: { to: '/equipes', chave: 'eq:recorte' },
        carregar: async () => {
            const itens = await EquipeService.buscarTodas();
            return itens.map(e => ({
                chave: `eq-${e.id}`,
                titulo: e.nome,
                subtitulo: linha(e.liderNome ? `Líder: ${e.liderNome}` : null,
                    `${e.membroIds.length} membro${e.membroIds.length === 1 ? '' : 's'}`),
                to: '/equipes',
                termos: [e.nome, e.liderNome],
            }));
        },
    },
    {
        id: 'ajuda',
        grupo: 'Ajuda',
        icone: LuCircleHelp,
        rota: '/help',
        /* A ÚNICA fonte que já busca no servidor: `/api/Help/Artigos?q=`. As
           outras não têm parâmetro de busca na API (ver a fase 3 da proposta),
           então filtram em memória. */
        servidor: true,
        carregar: async (texto: string) => {
            const itens = await HelpService.getArticles(texto);
            return itens.map(a => ({
                chave: `aj-${a.id}`,
                titulo: a.titulo,
                subtitulo: a.tags || undefined,
                to: `/help/article/${a.id}`,
                termos: [a.titulo, a.tags],
            }));
        },
    },
];

/* ─── AS TELAS ────────────────────────────────────────────────── */

export interface TelaBuscavel {
    chave: string;
    titulo: string;
    /** Onde ela mora, para quem conhece o nome mas não a seção. */
    secao?: string;
    icone: IconType;
    to: string;
    termos: string[];
}

/**
 * As telas que não estão nos dois mapas de navegação: o rodapé da sidebar e as
 * páginas que se alcança por botão. Sem elas, "tudo" não seria tudo — e são
 * justamente as que ninguém lembra onde ficam.
 */
const TELAS_AVULSAS: { titulo: string; to: string; icone: IconType; secao?: string; termos?: string[] }[] = [
    { titulo: 'Início', to: '/home', icone: LuHouse, termos: ['home', 'principal'] },
    { titulo: 'Primeiros passos', to: '/primeiros-passos', icone: LuRocket, termos: ['onboarding', 'começar'] },
    { titulo: 'Meu plano', to: '/plano', icone: LuCreditCard, secao: 'Conta', termos: ['assinatura', 'cobrança', 'pagamento'] },
    { titulo: 'Central de ajuda', to: '/help', icone: LuCircleHelp, termos: ['suporte', 'dúvida', 'tutorial'] },
    { titulo: 'Anotações', to: '/anotacoes', icone: LuNotebookPen, termos: ['notas'] },
    { titulo: 'Brik AI', to: '/assistente', icone: LuSparkles, secao: 'Brik AI', termos: ['ia', 'assistente', 'chat interno'] },
    { titulo: 'Novo relatório', to: '/relatorios/novo', icone: LuFileText, secao: 'Relatórios' },
];

export function telasBuscaveis(): TelaBuscavel[] {
    const telas: TelaBuscavel[] = [];
    const vistas = new Set<string>();

    const juntar = (titulo: string, to: string, icone: IconType, secao?: string, extras: string[] = []) => {
        if (vistas.has(to)) return;
        vistas.add(to);
        telas.push({ chave: `tela-${to}`, titulo, secao, icone, to, termos: [titulo, ...extras] });
    };

    GRUPOS_OPERACAO.forEach(grupo => {
        grupo.itens.forEach(item => juntar(item.rotulo, item.to, item.icone, grupo.titulo));
    });
    TELAS_AVULSAS.forEach(t => juntar(t.titulo, t.to, t.icone, t.secao, t.termos));
    /* Configurações entra com a seção no rótulo da busca ("Pessoas", "Empresa"):
       é o que diferencia "Equipes" a tela de "Equipes" o cadastro. */
    GRUPOS_CONFIG.forEach(grupo => {
        grupo.itens.forEach(entrada => {
            if (ehSubmenu(entrada)) {
                /* O submenu entra no rótulo ("Sistema · Relatórios"): "Mensagens"
                   sozinho se confundiria com as mensagens do Chat. */
                entrada.itens.forEach(item => juntar(item.rotulo, item.to, LuSettings,
                    `Configurações · ${grupo.titulo} · ${entrada.rotulo}`, [entrada.rotulo]));
                return;
            }
            juntar(entrada.rotulo, entrada.to, LuSettings, `Configurações · ${grupo.titulo}`);
        });
    });

    return telas;
}

/* ─── AS AÇÕES ────────────────────────────────────────────────── */

export interface AcaoBusca {
    chave: string;
    titulo: string;
    descricao: string;
    icone: IconType;
    /** A rota que a ação alcança — é por ela que a permissão é decidida. */
    rota: string;
    /** Navegar (o caso comum) ou executar algo no app. */
    to?: string;
    efeito?: 'tema' | 'sair';
    termos: string[];
}

export const ACOES: AcaoBusca[] = [
    {
        chave: 'ac-nova-oportunidade', titulo: 'Nova oportunidade', descricao: 'abrir um negócio',
        icone: LuPlus, rota: '/pipeline', to: '/pipeline',
        termos: ['nova oportunidade', 'novo negócio', 'criar negócio', 'cadastrar oportunidade', 'lead'],
    },
    {
        chave: 'ac-novo-contato', titulo: 'Novo contato', descricao: 'cadastrar uma pessoa',
        icone: LuPlus, rota: '/contacts', to: '/contacts',
        termos: ['novo contato', 'cadastrar pessoa', 'criar contato', 'cliente'],
    },
    {
        chave: 'ac-nova-organizacao', titulo: 'Nova organização', descricao: 'cadastrar uma empresa cliente',
        icone: LuPlus, rota: '/organizations', to: '/organizations',
        termos: ['nova organização', 'cadastrar empresa', 'criar organização'],
    },
    {
        chave: 'ac-nova-tarefa', titulo: 'Nova tarefa', descricao: 'lembrar de algo',
        icone: LuPlus, rota: '/tarefas', to: '/tarefas',
        termos: ['nova tarefa', 'criar tarefa', 'lembrete', 'to do'],
    },
    {
        chave: 'ac-nova-anotacao', titulo: 'Nova anotação', descricao: 'escrever uma nota',
        icone: LuPlus, rota: '/anotacoes', to: '/anotacoes/nova',
        termos: ['nova anotação', 'criar nota', 'escrever'],
    },
    {
        chave: 'ac-novo-produto', titulo: 'Novo produto', descricao: 'cadastrar um item',
        icone: LuPlus, rota: '/products', to: '/products',
        termos: ['novo produto', 'cadastrar produto', 'criar item'],
    },
    {
        chave: 'ac-nova-campanha', titulo: 'Nova campanha', descricao: 'disparar mensagens',
        icone: LuSend, rota: '/campanhas', to: '/campanhas/nova',
        termos: ['nova campanha', 'disparar', 'disparo', 'criar campanha'],
    },
    {
        chave: 'ac-novo-relatorio', titulo: 'Novo relatório', descricao: 'montar um painel',
        icone: LuFileText, rota: '/relatorios', to: '/relatorios/novo',
        termos: ['novo relatório', 'criar relatório', 'painel'],
    },
    {
        chave: 'ac-novo-usuario', titulo: 'Novo usuário', descricao: 'convidar alguém para a equipe',
        icone: LuPlus, rota: '/users', to: '/users',
        termos: ['novo usuário', 'convidar', 'adicionar pessoa', 'criar usuário'],
    },
    {
        chave: 'ac-nova-equipe', titulo: 'Nova equipe', descricao: 'agrupar pessoas',
        icone: LuUsersRound, rota: '/equipes', to: '/equipes',
        termos: ['nova equipe', 'criar equipe', 'time'],
    },
    {
        chave: 'ac-nova-meta', titulo: 'Nova meta', descricao: 'definir um alvo do mês',
        icone: LuGoal, rota: '/metas', to: '/metas',
        termos: ['nova meta', 'criar meta', 'objetivo', 'alvo'],
    },
    {
        chave: 'ac-conectar-canal', titulo: 'Conectar um canal', descricao: 'WhatsApp, Instagram',
        icone: LuMessageSquare, rota: '/connections', to: '/connections',
        termos: ['conectar', 'conexão', 'whatsapp', 'instagram', 'canal', 'qr code'],
    },
    {
        chave: 'ac-conectar-app', titulo: 'Conectar um app', descricao: 'RD Station, anúncios da Meta, webhook de entrada',
        icone: LuPlug, rota: '/apps', to: '/apps',
        termos: ['app', 'apps', 'integração', 'integrações', 'rd station', 'rd', 'marketing', 'anúncios', 'meta ads', 'marketplace', 'webhook', 'make', 'n8n', 'automação'],
    },
    {
        chave: 'ac-tema', titulo: 'Trocar o tema', descricao: 'claro ou escuro',
        icone: LuSun, rota: '/home', efeito: 'tema',
        termos: ['tema', 'escuro', 'claro', 'dark mode', 'aparência', 'modo noturno'],
    },
    {
        chave: 'ac-sair', titulo: 'Sair', descricao: 'encerrar a sessão',
        icone: LuLogOut, rota: '/home', efeito: 'sair',
        termos: ['sair', 'logout', 'desconectar', 'encerrar sessão'],
    },
];

/** O ícone da ação de tema depende do tema atual — quem desenha decide. */
export const ICONE_TEMA = { claro: LuMoon, escuro: LuSun };

/* ─── LEVAR O TERMO PARA A LISTAGEM ───────────────────────────── */

/**
 * Grava o texto buscado no recorte da tela de destino, **antes** de navegar.
 *
 * É assim que "Ver todos os contatos com «joão»" chega filtrado sem que a
 * `ContactsPage` saiba que a paleta existe: o recorte já é lido uma vez na
 * montagem (ver `hooks/useRecorte`), então basta deixá-lo pronto. Lê e mescla
 * em vez de sobrescrever — o recorte guarda também aba, ordem e página, e
 * jogar fora o filtro de cidade de quem buscou um nome seria trocar um
 * problema por outro.
 */
export function semearBusca(chave: string, texto: string, campo = 'busca'): void {
    try {
        const cru = sessionStorage.getItem(chave);
        const atual = cru ? JSON.parse(cru) : {};
        sessionStorage.setItem(chave, JSON.stringify({
            ...(atual && typeof atual === 'object' ? atual : {}),
            [campo]: texto,
            /* Filtro novo, primeira página: senão a pessoa cai na página 3 de
               um recorte que acabou de mudar e parece que não achou nada. */
            pagina: 1,
        }));
    } catch {
        /* Modo privado, cota estourada: segue sem o filtro — a tela abre
           inteira, que é o pior caso aceitável. */
    }
}
